import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import TapeEditModal from './components/TapeEditModal';
import BatchUpdateModal from './components/BatchUpdateModal';
import BatchCreateModal from './components/BatchCreateModal';
import CreatableSelect from '@/components/CreatableSelect';
import { useOffice } from '@/hooks/useOffice';
import { logActivity } from '@/lib/activityLog';

export interface Tape {
  id: number;
  media_label: string;
  media_set: string | null;
  modified_date: string | null;
  tape_type: string | null;
  location: string | null;
  box_number: string | null;
  backup_set: string | null;
  notes: string | null;
  needs_replacement?: boolean;
  damaged?: boolean;
  updated_at?: string;
}

type SortKey = 'media_label' | 'modified_date' | 'tape_type' | 'box_number';

function SortHeader({
  label,
  columnKey,
  activeKey,
  dir,
  onSort,
}: {
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
}) {
  const active = columnKey === activeKey;
  return (
    <button
      onClick={() => onSort(columnKey)}
      className="inline-flex items-center gap-1 hover:text-slate-700 cursor-pointer whitespace-nowrap"
      title={`Sort by ${label}`}
    >
      {label}
      <i
        className={
          active
            ? dir === 'asc'
              ? 'ri-arrow-up-line w-4 h-4 flex items-center justify-center'
              : 'ri-arrow-down-line w-4 h-4 flex items-center justify-center'
            : 'ri-arrow-up-down-line w-4 h-4 flex items-center justify-center text-slate-300'
        }
      />
    </button>
  );
}

export default function TapesPage() {
  const { currentOfficeId } = useOffice();
  const [tapes, setTapes] = useState<Tape[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [fType, setFType] = useState('');
  const [searchParams] = useSearchParams();
  const [fLoc, setFLoc] = useState(searchParams.get('location') || '');
  const [fSet, setFSet] = useState('');
  const [fBackupSet, setFBackupSet] = useState('');
  const [fBox, setFBox] = useState('');
  const [fReplacement, setFReplacement] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('media_label');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Tape | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showBatchCreate, setShowBatchCreate] = useState(false);
  const [batchField, setBatchField] = useState<'box_number' | 'location' | 'tape_type' | null>(null);
  const [boxNumbers, setBoxNumbers] = useState<string[]>([]);
  const [masterOptions, setMasterOptions] = useState<Record<string, string[]>>({
    media_set: [],
    tape_type: [],
    location: [],
    backup_set: [],
  });

  const load = async () => {
    if (!currentOfficeId) return;
    setLoading(true); setError('');
    try {
      const { data, error } = await supabase.from('tapes').select('*').eq('office_id', currentOfficeId).order('id', { ascending: true });
      if (error) throw error;
      setTapes(data as Tape[]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // When switching offices, clear the previous office's tapes and reset all
  // filters immediately so stale data never lingers while the new office loads.
  useEffect(() => {
    setTapes([]);
    setQ('');
    setFType('');
    setFLoc('');
    setFSet('');
    setFBackupSet('');
    setFBox('');
    setFReplacement('');
    setPage(1);
    setSelectedIds(new Set());
    load();
  }, [currentOfficeId]);

  useEffect(() => {
    if (!currentOfficeId) return;
    const loadBoxes = async () => {
      const { data, error } = await supabase.from('boxes').select('box_number').eq('office_id', currentOfficeId).order('box_number');
      if (error) return;
      setBoxNumbers(Array.from(new Set((data as { box_number: string }[]).map((b) => b.box_number).filter(Boolean))));
    };
    loadBoxes();
  }, [currentOfficeId]);

  useEffect(() => {
    if (!currentOfficeId) return;
    const loadOptions = async () => {
      const { data, error } = await supabase.from('tape_options').select('category, value').eq('office_id', currentOfficeId).order('created_at');
      if (error) return;
      const grouped: Record<string, string[]> = { media_set: [], tape_type: [], location: [], backup_set: [] };
      (data as { category: string; value: string }[]).forEach((r) => {
        if (grouped[r.category] && r.value && !grouped[r.category].includes(r.value)) {
          grouped[r.category].push(r.value);
        }
      });
      setMasterOptions(grouped);
    };
    loadOptions();
  }, [currentOfficeId]);

  const options = useMemo(() => ({
    types: Array.from(new Set(tapes.map((t) => t.tape_type).filter(Boolean))) as string[],
    locations: Array.from(new Set(tapes.map((t) => t.location).filter(Boolean))) as string[],
    sets: Array.from(new Set(tapes.map((t) => t.media_set).filter(Boolean))) as string[],
    backupSets: Array.from(new Set(tapes.map((t) => t.backup_set).filter(Boolean))) as string[],
  }), [tapes]);

  const allOptions = useMemo(() => ({
    types: Array.from(new Set([...options.types, ...masterOptions.tape_type])),
    locations: Array.from(new Set([...options.locations, ...masterOptions.location])),
    sets: Array.from(new Set([...options.sets, ...masterOptions.media_set])),
    backupSets: Array.from(new Set([...options.backupSets, ...masterOptions.backup_set])),
  }), [options, masterOptions]);

  // Box number filter options: combine box numbers from the boxes table with
  // any box numbers already present on tapes, dedupe and sort numerically.
  const boxNumberOptions = useMemo(() => {
    const fromTapes = tapes.map((t) => t.box_number).filter(Boolean) as string[];
    return Array.from(new Set([...boxNumbers, ...fromTapes])).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [tapes, boxNumbers]);

  // Cascading filter options: each dropdown only shows values that exist among
  // tapes already matching the OTHER active filters (excluding its own filter),
  // so selecting a Media Set narrows Tape Type / Location / Backup Set choices.
  const cascadeOptions = useMemo(() => {
    const filterExcept = (except: string) => tapes.filter((t) => {
      if (except !== 'type' && fType && t.tape_type !== fType) return false;
      if (except !== 'loc' && fLoc && t.location !== fLoc) return false;
      if (except !== 'set' && fSet && t.media_set !== fSet) return false;
      if (except !== 'backupSet' && fBackupSet && t.backup_set !== fBackupSet) return false;
      if (except !== 'replacement') {
        if (fReplacement === 'needs' && !t.needs_replacement) return false;
        if (fReplacement === 'ok' && !!t.needs_replacement) return false;
      }
      return true;
    });
    const uniq = (arr: (string | null)[]) => Array.from(new Set(arr.filter(Boolean))) as string[];
    return {
      sets: uniq(filterExcept('set').map((t) => t.media_set)),
      backupSets: uniq(filterExcept('backupSet').map((t) => t.backup_set)),
      types: uniq(filterExcept('type').map((t) => t.tape_type)),
      locations: uniq(filterExcept('loc').map((t) => t.location)),
    };
  }, [tapes, fType, fLoc, fSet, fBackupSet, fReplacement]);

  const filtered = useMemo(() => tapes.filter((t) => {
    if (q && !t.media_label.toLowerCase().includes(q.toLowerCase()) && !(t.box_number || '').toLowerCase().includes(q.toLowerCase())) return false;
    if (fType && t.tape_type !== fType) return false;
    if (fLoc && t.location !== fLoc) return false;
    if (fSet && t.media_set !== fSet) return false;
    if (fBackupSet && t.backup_set !== fBackupSet) return false;
    if (fBox && t.box_number !== fBox) return false;
    if (fReplacement === 'needs' && !t.needs_replacement) return false;
    if (fReplacement === 'ok' && !!t.needs_replacement) return false;
    return true;
  }), [tapes, q, fType, fLoc, fSet, fBackupSet, fBox, fReplacement]);

  const sortedFiltered = useMemo(() => {
    const getVal = (t: Tape): string => {
      if (sortKey === 'media_label') return t.media_label || '';
      if (sortKey === 'modified_date') return t.modified_date || '';
      if (sortKey === 'tape_type') return t.tape_type || '';
      return t.box_number || '';
    };
    const sorted = [...filtered].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      const aEmpty = !va;
      const bEmpty = !vb;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      const cmp = va.toLowerCase().localeCompare(vb.toLowerCase());
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const effectivePageSize = pageSize === 0 ? sortedFiltered.length : pageSize;
  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = sortedFiltered.slice((currentPage - 1) * effectivePageSize, currentPage * effectivePageSize);

  const toggleReplacement = async (id: number, current: boolean) => {
    const next = !current;
    const { error } = await supabase
      .from('tapes')
      .update({ needs_replacement: next, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { alert(error.message); return; }
    setTapes((s) => s.map((t) => t.id === id ? { ...t, needs_replacement: next } : t));
    const t = tapes.find((x) => x.id === id);
    await logActivity('flag_replacement', next ? `Flagged ${t?.media_label ?? 'tape'} for replacement` : `Cleared replacement flag for ${t?.media_label ?? 'tape'}`);
  };

  // Select-all state for the current page's "Tape Replacement" column.
  const allPageChecked = pageData.length > 0 && pageData.every((t) => !!t.needs_replacement);
  const somePageChecked = pageData.some((t) => !!t.needs_replacement);
  const replacementHeaderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (replacementHeaderRef.current) {
      replacementHeaderRef.current.indeterminate = !allPageChecked && somePageChecked;
    }
  }, [allPageChecked, somePageChecked]);

  const toggleAllReplacement = async () => {
    if (pageData.length === 0) return;
    const ids = pageData.map((t) => t.id);
    const next = !allPageChecked;
    const { error } = await supabase
      .from('tapes')
      .update({ needs_replacement: next, updated_at: new Date().toISOString() })
      .in('id', ids);
    if (error) { alert(error.message); return; }
    setTapes((s) => s.map((t) => ids.includes(t.id) ? { ...t, needs_replacement: next } : t));
    await logActivity('flag_replacement', next ? `Flagged ${ids.length} tape(s) for replacement` : `Cleared replacement flag for ${ids.length} tape(s)`);
  };

  // Selection state for batch delete.
  const allSelected = pageData.length > 0 && pageData.every((t) => selectedIds.has(t.id));
  const someSelected = pageData.some((t) => selectedIds.has(t.id));
  const selectHeaderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectHeaderRef.current) {
      selectHeaderRef.current.indeterminate = !allSelected && someSelected;
    }
  }, [allSelected, someSelected]);

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageData.forEach((t) => next.delete(t.id));
      } else {
        pageData.forEach((t) => next.add(t.id));
      }
      return next;
    });
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} selected tape(s)? This action cannot be undone.`)) return;
    const { error } = await supabase.from('tapes').delete().in('id', ids);
    if (error) { alert(error.message); return; }
    setTapes((s) => s.filter((t) => !selectedIds.has(t.id)));
    setSelectedIds(new Set());
    await logActivity('delete_tapes_batch', `Deleted ${ids.length} tape(s)`);
  };

  const toggleDamaged = async (id: number, current: boolean) => {
    const next = !current;
    const { error } = await supabase
      .from('tapes')
      .update({ damaged: next, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { alert(error.message); return; }
    setTapes((s) => s.map((t) => t.id === id ? { ...t, damaged: next } : t));
    const t = tapes.find((x) => x.id === id);
    await logActivity('flag_damaged', next ? `Marked ${t?.media_label ?? 'tape'} as damaged` : `Cleared damaged flag for ${t?.media_label ?? 'tape'}`);
  };

  const del = async (id: number) => {
    if (!confirm('Delete this tape?')) return;
    const { error } = await supabase.from('tapes').delete().eq('id', id);
    if (error) { alert(error.message); return; }
    const removed = tapes.find((t) => t.id === id);
    setTapes((s) => s.filter((t) => t.id !== id));
    if (removed) await logActivity('delete_tape', `Deleted tape ${removed.media_label}`);
  };

  const persistOption = async (category: string, value: string) => {
    setMasterOptions((prev) => ({
      ...prev,
      [category]: prev[category].includes(value) ? prev[category] : [...prev[category], value],
    }));
    const { error } = await supabase.from('tape_options').insert({ category, value, office_id: currentOfficeId });
    if (error) console.error('Failed to save option', error);
  };

  const removeMasterOption = async (category: string, value: string) => {
    setMasterOptions((prev) => ({
      ...prev,
      [category]: prev[category].filter((v) => v !== value),
    }));
    const { error } = await supabase.from('tape_options').delete().eq('category', category).eq('value', value).eq('office_id', currentOfficeId);
    if (error) console.error('Failed to remove option', error);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Tapes</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} tapes</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNew(true)}
            className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line w-4 h-4 flex items-center justify-center" /> New Tape
          </button>
          <button
            onClick={() => setShowBatchCreate(true)}
            className="px-4 py-2 rounded-md border border-slate-200 text-slate-700 text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 hover:bg-slate-50"
          >
            <i className="ri-stack-line w-4 h-4 flex items-center justify-center" /> Batch Create
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 mb-4 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 flex items-center justify-center" />
          <input
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search media label or box number..."
            className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
          />
          {q && (
            <button
              type="button"
              aria-label="Clear search"
              title="Clear search"
              onClick={() => { setQ(''); setPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
            </button>
          )}
        </div>
        <div className="w-48">
          <CreatableSelect
            value={fSet}
            onChange={(v) => { setFSet(v); setPage(1); }}
            options={masterOptions.media_set}
            defaultOptions={cascadeOptions.sets}
            replaceOnChange
            onAdd={(name) => persistOption('media_set', name)}
            onRemove={(name) => removeMasterOption('media_set', name)}
            placeholder="All Media Sets"
            clearable
          />
        </div>
        <div className="w-48">
          <CreatableSelect
            value={fBackupSet}
            onChange={(v) => { setFBackupSet(v); setPage(1); }}
            options={masterOptions.backup_set}
            defaultOptions={cascadeOptions.backupSets}
            replaceOnChange
            onAdd={(name) => persistOption('backup_set', name)}
            onRemove={(name) => removeMasterOption('backup_set', name)}
            placeholder="All Backup Sets"
            clearable
          />
        </div>
        <div className="w-48">
          <CreatableSelect
            value={fType}
            onChange={(v) => { setFType(v); setPage(1); }}
            options={masterOptions.tape_type}
            defaultOptions={cascadeOptions.types}
            replaceOnChange
            onAdd={(name) => persistOption('tape_type', name)}
            onRemove={(name) => removeMasterOption('tape_type', name)}
            placeholder="All Tape Types"
            clearable
          />
        </div>
        <div className="w-48">
          <CreatableSelect
            value={fLoc}
            onChange={(v) => { setFLoc(v); setPage(1); }}
            options={masterOptions.location}
            defaultOptions={cascadeOptions.locations}
            replaceOnChange
            onAdd={(name) => persistOption('location', name)}
            onRemove={(name) => removeMasterOption('location', name)}
            placeholder="All Locations"
            clearable
          />
        </div>
        <div className="w-44">
          <select
            value={fBox}
            onChange={(e) => { setFBox(e.target.value); setPage(1); }}
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
          >
            <option value="">All Box Numbers</option>
            {boxNumberOptions.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
        <div className="w-40">
          <select
            value={fReplacement}
            onChange={(e) => { setFReplacement(e.target.value); setPage(1); }}
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
          >
            <option value="">All Status</option>
            <option value="needs">Needs Replacement</option>
            <option value="ok">OK</option>
          </select>
        </div>
        {(q || fType || fLoc || fSet || fBackupSet || fBox || fReplacement) && (
          <button onClick={() => { setQ(''); setFType(''); setFLoc(''); setFSet(''); setFBackupSet(''); setFBox(''); setFReplacement(''); }}
            className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer whitespace-nowrap">Clear filters</button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer">Retry</button>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between mb-4 px-4 py-3 bg-slate-900 text-white rounded-lg">
          <span className="text-sm">{selectedIds.size} tape(s) selected</span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-slate-300 hover:text-white cursor-pointer whitespace-nowrap"
            >
              Clear selection
            </button>
            <button
              onClick={() => setBatchField('tape_type')}
              className="px-3 py-1.5 rounded-md bg-white text-slate-900 text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center" />
              Change Tape Type
            </button>
            <button
              onClick={() => setBatchField('box_number')}
              className="px-3 py-1.5 rounded-md bg-white text-slate-900 text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-archive-line w-4 h-4 flex items-center justify-center" />
              Change Box Number
            </button>
            <button
              onClick={() => setBatchField('location')}
              className="px-3 py-1.5 rounded-md bg-white text-slate-900 text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center" />
              Change Location
            </button>
            <button
              onClick={deleteSelected}
              className="px-3 py-1.5 rounded-md bg-white text-slate-900 text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.slate.200)]">
            <tr>
              <th className="text-center px-4 py-2.5 font-medium w-10 rounded-tl-lg">
                <input
                  ref={selectHeaderRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 cursor-pointer accent-slate-900"
                  title="Select / deselect all tapes on this page"
                />
              </th>
              <th className="text-left px-4 py-2.5 font-medium">
                <SortHeader label="Media Label" columnKey="media_label" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Media Set</th>
              <th className="text-left px-4 py-2.5 font-medium">Backup Set</th>
              <th className="text-left px-4 py-2.5 font-medium">
                <SortHeader label="Modified" columnKey="modified_date" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              </th>
              <th className="text-left px-4 py-2.5 font-medium">
                <SortHeader label="Tape Type" columnKey="tape_type" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Location</th>
              <th className="text-left px-4 py-2.5 font-medium">
                <SortHeader label="Box Number" columnKey="box_number" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              </th>
              <th className="text-center px-4 py-2.5 font-medium">
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title="Select / deselect all tapes on this page">
                  <input
                    ref={replacementHeaderRef}
                    type="checkbox"
                    checked={allPageChecked}
                    onChange={toggleAllReplacement}
                    className="w-4 h-4 cursor-pointer accent-rose-600"
                  />
                  Tape Replacement
                </label>
              </th>
              <th className="text-center px-4 py-2.5 font-medium">Damaged</th>
              <th className="text-right px-4 py-2.5 font-medium rounded-tr-lg">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400 text-sm">Loading...</td></tr>
            )}
            {!loading && pageData.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(t.id)}
                    onChange={() => toggleSelect(t.id)}
                    className="w-4 h-4 cursor-pointer accent-slate-900"
                    title="Select this tape"
                  />
                </td>
                <td className="px-4 py-2.5 font-medium">{t.media_label}</td>
                <td className="px-4 py-2.5 text-slate-600">{t.media_set || '—'}</td>
                <td className="px-4 py-2.5 text-slate-600">{t.backup_set || '—'}</td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{t.modified_date || '—'}</td>
                <td className="px-4 py-2.5">
                  {t.tape_type ? <span className="inline-block px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">{t.tape_type}</span> : '—'}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{t.location || '—'}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{t.box_number || '—'}</td>
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={!!t.needs_replacement}
                    onChange={() => toggleReplacement(t.id, !!t.needs_replacement)}
                    className="w-4 h-4 cursor-pointer accent-rose-600"
                    title={t.needs_replacement ? 'Marked for replacement' : 'Mark for replacement'}
                  />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={!!t.damaged}
                    onChange={() => toggleDamaged(t.id, !!t.damaged)}
                    className="w-4 h-4 cursor-pointer accent-red-600"
                    title={t.damaged ? 'Marked as damaged' : 'Mark as damaged'}
                  />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => setEditing(t)} className="text-slate-500 hover:text-slate-900 cursor-pointer mr-3" title="Edit">
                    <i className="ri-edit-line w-4 h-4 flex items-center justify-center inline-block" />
                  </button>
                  <button onClick={() => del(t.id)} className="text-slate-500 hover:text-red-600 cursor-pointer" title="Delete">
                    <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center inline-block" />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && pageData.length === 0 && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400 text-sm">No tapes found</td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500 flex-wrap gap-2">
            <span>
              {pageSize === 0
                ? `${filtered.length} records`
                : `Page ${currentPage} of ${totalPages}`}
            </span>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span>Show</span>
                <select
                  value={String(pageSize)}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="text-xs px-2 py-1 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 bg-white cursor-pointer"
                >
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="0">All</option>
                </select>
                <span>per page</span>
              </label>
              <div className="flex gap-2">
                <button disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}
                  className="px-3 py-1 border border-slate-200 rounded-md disabled:opacity-40 cursor-pointer whitespace-nowrap">Prev</button>
                <button disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}
                  className="px-3 py-1 border border-slate-200 rounded-md disabled:opacity-40 cursor-pointer whitespace-nowrap">Next</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {(editing || showNew) && (
        <TapeEditModal
          tape={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { setEditing(null); setShowNew(false); load(); }}
          allMediaSets={allOptions.sets}
          allBackupSets={allOptions.backupSets}
          allTapeTypes={allOptions.types}
          allLocations={allOptions.locations}
          allBoxNumbers={boxNumberOptions}
        />
      )}

      {showBatchCreate && (
        <BatchCreateModal
          onClose={() => setShowBatchCreate(false)}
          onDone={() => { setShowBatchCreate(false); load(); }}
          allMediaSets={allOptions.sets}
          allBackupSets={allOptions.backupSets}
          allTapeTypes={allOptions.types}
          allLocations={allOptions.locations}
          allBoxNumbers={boxNumberOptions}
        />
      )}

      {batchField && (
        <BatchUpdateModal
          field={batchField}
          options={batchField === 'box_number' ? boxNumberOptions : batchField === 'tape_type' ? allOptions.types : allOptions.locations}
          ids={Array.from(selectedIds)}
          onClose={() => setBatchField(null)}
          onDone={() => { setBatchField(null); setSelectedIds(new Set()); load(); }}
        />
      )}
    </div>
  );
}
